<script setup lang="ts">
  import { computed, getCurrentInstance, onBeforeUnmount } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import V2StoreHoursEditorModal from '@/components/v2/store-setup/business/V2StoreHoursEditorModal.vue';
  import { useModal } from '@/composition/use-modal';
  import { HOURS_EDITOR_MODAL } from '@/domain/store-setup/hours-editor';
  import type { IHoursEditorResult } from '@/domain/store-setup/hours-editor';
  import {
    defaultWeek,
    hoursDisplayRows,
  } from '@/domain/store-setup/opening-hours';
  import type { TWeekHours } from '@/domain/store-setup/opening-hours';

  /**
   * «¿Qué días abre?» (plan §4.2 item 4, D9): the hours grouped by runs of
   * days with «Está bien» / «Cambiar», or «Agregar horario» when there are
   * none. What is shown is what «Crear mi tienda» saves, confirmed or not.
   */
  const EDITOR_WIDTH = 520;

  const props = withDefaults(
    defineProps<{
      value?: TWeekHours | null;
      fromGoogle: boolean;
      confirmed: boolean;
      disabled?: boolean;
    }>(),
    { value: null, disabled: false }
  );
  const emit = defineEmits<{
    (e: 'input', week: TWeekHours | null): void;
    (e: 'confirm'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const modals = useModal();
  let alive = true;
  let editorOpen = false;

  const locale = computed(() => proxy.$i18n.locale);
  const rows = computed(() =>
    props.value
      ? hoursDisplayRows(props.value, locale.value, {
          all: tr('storeSetup.business.everyDay'),
          fromTo: (from, to) =>
            tr('storeSetup.business.dayRange', { from, to }),
          list: (list, last) =>
            tr('storeSetup.business.dayList', { list, last }),
          closed: tr('storeSetup.business.closed'),
        })
      : []
  );

  /** The editor on the current hours («Cambiar») or on Monday–Saturday 12:00–23:00 («Agregar horario»). */
  async function openEditor() {
    if (props.disabled || editorOpen) return;
    editorOpen = true;
    try {
      const result = await modals.showDynamicDialog<IHoursEditorResult | null>(
        V2StoreHoursEditorModal,
        { value: props.value ?? defaultWeek() },
        HOURS_EDITOR_MODAL,
        { width: EDITOR_WIDTH, clickToClose: false }
      );
      // Cancelled (null), or this step went away while the editor was open.
      if (!alive || result === null) return;
      emit('input', result.week);
    } finally {
      editorOpen = false;
    }
  }

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <div class="v2-setup-hours-field">
    <p class="hours-label">
      {{ $t('storeSetup.business.hoursQuestion') }}
      <span v-if="fromGoogle && value" class="hours-source">{{
        $t('storeSetup.business.fromGoogle')
      }}</span>
    </p>
    <div v-if="value" class="hours-box">
      <dl class="hours-rows">
        <div v-for="row in rows" :key="row.days" class="hours-row">
          <dt class="row-days">{{ row.days }}</dt>
          <dd class="row-hours">{{ row.hours }}</dd>
        </div>
      </dl>
      <div class="hours-actions">
        <span v-if="confirmed" class="hours-confirmed">
          <PosUiIcon name="check" class="confirmed-icon" />
          {{ $t('storeSetup.business.hoursConfirmed') }}
        </span>
        <PosUiButton
          v-else
          color="success"
          variant="soft"
          size="sm"
          class="hours-ok"
          :disabled="disabled"
          @click="emit('confirm')"
          >{{ $t('storeSetup.business.hoursOk') }}</PosUiButton
        >
        <PosUiButton
          color="neutral"
          variant="ghost"
          size="sm"
          class="hours-change"
          :disabled="disabled"
          @click="openEditor"
          >{{ $t('storeSetup.business.hoursChange') }}</PosUiButton
        >
      </div>
    </div>
    <div v-else class="hours-box is-empty">
      <p class="hours-none">{{ $t('storeSetup.business.hoursNone') }}</p>
      <PosUiButton
        color="neutral"
        variant="outline"
        size="sm"
        icon="clock-outline"
        class="hours-add"
        :disabled="disabled"
        @click="openEditor"
        >{{ $t('storeSetup.business.hoursAdd') }}</PosUiButton
      >
    </div>
    <p class="hours-hint">
      {{
        value
          ? $t('storeSetup.business.hoursHint')
          : $t('storeSetup.business.hoursNoneHint')
      }}
    </p>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-hours-field {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-2;
    .hours-label {
      @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .hours-source {
      @apply tw-text-xs tw-font-normal tw-text-neutral-500;
    }
    .hours-box {
      @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-px-4 tw-py-3;
      &.is-empty {
        @apply tw-flex-row tw-flex-wrap tw-items-center tw-justify-between tw-gap-2;
      }
    }
    .hours-rows {
      @apply tw-m-0 tw-flex tw-flex-col tw-gap-1.5 tw-text-sm;
    }
    .hours-row {
      @apply tw-flex tw-flex-wrap tw-justify-between tw-gap-x-3 tw-gap-y-0.5;
    }
    .row-days {
      @apply tw-text-neutral-900;
    }
    .row-hours {
      @apply tw-m-0 tw-tabular-nums tw-text-neutral-700;
    }
    .hours-actions {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
    }
    // «sm» buttons in a touch flow still get a 44 px hit area.
    .hours-ok,
    .hours-change,
    .hours-add {
      @apply tw-min-h-11;
    }
    .hours-confirmed {
      @apply tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-emerald-50 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-emerald-700;
    }
    .confirmed-icon {
      @apply tw-text-sm;
    }
    .hours-none {
      @apply tw-m-0 tw-text-sm tw-text-neutral-700;
    }
    .hours-hint {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
  }
</style>
